import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, CheckCircle, FileText, UserPlus, Users } from 'lucide-react';

export default function GeneralFormPage() {
  const { code } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [responses, setResponses] = useState({});
  const [selectedTenantId, setSelectedTenantId] = useState('new');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await publicAPI.getGeneralForm(code);
        setForm(res.data);
        const initial = {};
        res.data.fields?.forEach(f => {
          initial[f.name] = f.type === 'checkbox' ? false : '';
        });
        setResponses(initial);
      } catch {
        toast.error('Form not found or invalid code');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code]);

  const handleTenantSelect = (value) => {
    setSelectedTenantId(value);
    if (value !== 'new') {
      const tenant = form.tenants?.find(t => t.user_id === value);
      if (tenant) {
        setResponses(prev => ({
          ...prev,
          full_name: tenant.name || '',
          email: tenant.email || '',
        }));
      }
    } else {
      setResponses(prev => ({ ...prev, full_name: '', email: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!responses.full_name?.trim()) {
      toast.error('Full name (signature) is required');
      return;
    }
    const missing = form.fields?.filter(f => f.required && !responses[f.name]);
    if (missing?.length) {
      toast.error(`Please fill in: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...responses,
        _selected_tenant_id: selectedTenantId !== 'new' ? selectedTenantId : null,
        _is_new_tenant: selectedTenantId === 'new',
      };
      await publicAPI.submitGeneralForm(code, payload);
      setSubmitted(true);
      toast.success('Form submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="text-2xl font-bold">Thank You!</h2>
            <p className="text-muted-foreground">Your information has been submitted successfully.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8"><p className="text-muted-foreground">Form not found or invalid link.</p></CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center border-b">
            <div className="flex items-center justify-center gap-2 mb-2">
              <FileText className="w-6 h-6 text-[#C85A42]" />
              <CardTitle className="text-xl" data-testid="general-form-title">{form.title}</CardTitle>
            </div>
            {form.landlord_name && (
              <p className="text-sm text-muted-foreground">Managed by: {form.landlord_name}</p>
            )}
            <p className="text-sm text-muted-foreground mt-1">{form.description}</p>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Tenant Selection */}
              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg space-y-3">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" /> Select Your Name
                </Label>
                <Select value={selectedTenantId} onValueChange={handleTenantSelect} data-testid="tenant-select">
                  <SelectTrigger data-testid="tenant-select-trigger">
                    <SelectValue placeholder="Select your name..." />
                  </SelectTrigger>
                  <SelectContent>
                    {form.tenants?.map(t => (
                      <SelectItem key={t.user_id} value={t.user_id}>{t.name}{t.email ? ` (${t.email})` : ''}</SelectItem>
                    ))}
                    <SelectItem value="new">
                      <span className="flex items-center gap-1"><UserPlus className="w-3 h-3" /> I'm a new tenant</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {selectedTenantId === 'new' && (
                  <p className="text-xs text-blue-700 dark:text-blue-300">You'll be registered as a new tenant when you submit this form.</p>
                )}
              </div>

              {/* Form Fields */}
              {form.fields?.map(field => (
                <div key={field.name} className="space-y-2">
                  <Label htmlFor={field.name} className="text-sm font-medium">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </Label>

                  {(field.type === 'text' || field.type === 'email') && (
                    <Input
                      id={field.name}
                      type={field.type}
                      value={responses[field.name] || ''}
                      onChange={(e) => setResponses({ ...responses, [field.name]: e.target.value })}
                      required={field.required}
                      data-testid={`form-field-${field.name}`}
                    />
                  )}

                  {field.type === 'date' && (
                    <Input
                      id={field.name}
                      type="date"
                      value={responses[field.name] || ''}
                      onChange={(e) => setResponses({ ...responses, [field.name]: e.target.value })}
                      required={field.required}
                      data-testid={`form-field-${field.name}`}
                    />
                  )}

                  {field.type === 'textarea' && (
                    <Textarea
                      id={field.name}
                      value={responses[field.name] || ''}
                      onChange={(e) => setResponses({ ...responses, [field.name]: e.target.value })}
                      required={field.required}
                      data-testid={`form-field-${field.name}`}
                    />
                  )}

                  {field.type === 'select' && (
                    <Select
                      value={responses[field.name] || ''}
                      onValueChange={(v) => setResponses({ ...responses, [field.name]: v })}
                    >
                      <SelectTrigger data-testid={`form-field-${field.name}`}>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options?.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ))}

              {/* Signature note */}
              <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  By submitting this form, you confirm that the information provided is accurate. Your full name serves as your digital signature.
                </p>
              </div>

              <Button type="submit" className="w-full bg-[#C85A42] hover:bg-[#B54A35]" disabled={submitting} data-testid="submit-general-form-btn">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</> : 'Submit Form'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
